import React from 'react';
import { OstNode } from 'types';

/**
 * Unified status circle fill colours shared by all node types.
 * Solution,  story, and experiment statuses are mapped to the same four
 * semantic colours so circles look identical when nodes are at equivalent stages.
 * Opportunity nodes use validation colours instead of a status value.
 */
const STATUS_CIRCLE_COLORS = {
    solution: {
        Idea: '#93c5fd',        // light blue — earliest stage
        Validated: '#10b981',   // teal green — confirmed
        'In progress': '#f59e0b', // amber — active work
        Done: '#6b7280',        // gray — closed
    },
    story: {
        Draft: '#93c5fd',            // same as Idea
        'Ready to build': '#10b981', // same as Validated
        'Pushed to GitHub': '#f59e0b', // same as In progress
        Done: '#6b7280',             // same as Done
    },
    experiment: {
        Draft: '#93c5fd',
        'Ready to build': '#10b981',
        'Pushed to GitHub': '#f59e0b',
        Done: '#6b7280',
    },
    opportunity: {
        '✅': '#10b981', // validated
        '⚠️': '#f59e0b', // partial
        '❌': '#ef4444', // assumed / not validated
    },
} as const;

export function getStatusCircleFill(node: OstNode): string | null {
    if (node.type === 'solution') {
        return STATUS_CIRCLE_COLORS.solution[node.status as keyof typeof STATUS_CIRCLE_COLORS.solution] || null;
    }

    if (node.type === 'story') {
        return STATUS_CIRCLE_COLORS.story[node.status as keyof typeof STATUS_CIRCLE_COLORS.story] || null;
    }

    if (node.type === 'experiment') {
        return STATUS_CIRCLE_COLORS.experiment[node.status as keyof typeof STATUS_CIRCLE_COLORS.experiment] || null;
    }

    if (node.type === 'opportunity') {
        const validated = node.validated || '';
        if (validated.startsWith('✅')) return STATUS_CIRCLE_COLORS.opportunity['✅'];
        if (validated.startsWith('⚠️')) return STATUS_CIRCLE_COLORS.opportunity['⚠️'];
        if (validated.startsWith('❌')) return STATUS_CIRCLE_COLORS.opportunity['❌'];
    }

    return null;
}

/** Human-readable status bucket for a node, used to group nodes for status breakdowns (e.g. pie charts). */
function getStatusLabel(node: OstNode): string {
    if (node.type === 'solution' || node.type === 'story' || node.type === 'experiment') {
        return node.status || 'Unset';
    }

    if (node.type === 'opportunity') {
        const validated = node.validated || '';
        if (validated.startsWith('✅')) return 'Validated';
        if (validated.startsWith('⚠️')) return 'Partial';
        if (validated.startsWith('❌')) return 'Not validated';
        return 'Unset';
    }

    return 'Unset';
}

export type StatusSegment = { label: string; value: number; color: string };

/** Groups nodes by their status bucket, pairing each bucket with the same fill colour used by StatusCircle. */
export function getStatusSegments(nodes: OstNode[]): StatusSegment[] {
    const buckets = new Map<string, StatusSegment>();

    nodes.forEach((node) => {
        const label = getStatusLabel(node);
        const existing = buckets.get(label);
        if (existing) {
            existing.value += 1;
            return;
        }
        buckets.set(label, { label, value: 1, color: getStatusCircleFill(node) || '#d1d5db' });
    });

    return Array.from(buckets.values());
}

type StatusCircleProps = {
    node: OstNode;
    cx: number;
    cy: number;
    r?: number;
};

export default function StatusCircle({ node, cx, cy, r = 6 }: StatusCircleProps) {
    const fill = getStatusCircleFill(node);

    return (
        <circle
            className='status-circle'
            cx={cx}
            cy={cy}
            r={r}
            fill={fill || 'none'}
        />
    );
}
